import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { MouseGlow } from '@/components/site/MouseGlow';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { CTABand } from '@/components/site/CTABand';
import { WORK } from '@/data/work';
import WORK_SHOTS from '@/data/workShots.json';

function Shot({ shot, name, eager }) {
  return (
    <figure className={shot.mobile ? 'w-[240px] shrink-0' : 'w-full'}>
      <div className="overflow-hidden rounded-[16px] border border-white/[0.12] bg-ink-surface">
        <img
          src={shot.src}
          alt={`${name}: ${shot.label}`}
          width={shot.width}
          height={shot.height}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          className="block w-full h-auto"
        />
      </div>
      <figcaption className="mt-3 text-sm text-ink-muted">{shot.label}</figcaption>
    </figure>
  );
}

export default function WorkDetail({ slug }) {
  const w = WORK.find((p) => p.slug === slug);
  const shots = WORK_SHOTS[slug] || [];
  const desktop = shots.filter((s) => !s.mobile);
  const mobile = shots.filter((s) => s.mobile);
  const others = WORK.filter((p) => p.slug !== slug);
  const facts = [
    ['Sector', w.sector],
    ['What we built', w.kind],
    ['Status', 'Live'],
  ];

  return (
    <div data-testid={`work-detail-${slug}`}>
      <section className="relative overflow-hidden">
        <MouseGlow intensity={0.28} />
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-16 md:pb-20">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-ink-muted">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link to="/work/" className="hover:text-white transition-colors">Work</Link>
            <ChevronRight size={12} />
            <span className="text-white">{w.name}</span>
          </nav>
          <SectionLabel as="h1" num="—" className="mt-8" data-testid={`work-detail-${slug}-h1`}>
            {w.h1}
          </SectionLabel>
          <p className="mt-8 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tightest leading-[1.04] text-balance max-w-5xl">
            {w.summary}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <MagneticButton href={w.url} external variant="primary">
              Visit {w.domain} <ArrowUpRight size={16} />
            </MagneticButton>
            <MagneticButton to="/contact/" variant="ghost">Discuss a similar build</MagneticButton>
          </div>
          <dl className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl">
            {facts.map(([k, v]) => (
              <div key={k} className="border-l border-white/[0.12] pl-5">
                <dt className="text-[11px] uppercase tracking-[0.22em] text-ink-muted">{k}</dt>
                <dd className="mt-2 text-white/90">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {desktop[0] && (
        <section className="relative pb-20 md:pb-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Shot shot={desktop[0]} name={w.name} eager />
          </div>
        </section>
      )}

      <section className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="01">The project</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">What {w.name} is</h2>
          </div>
          <div className="md:col-span-8 space-y-6">
            {w.overview.map((para, i) => (
              <Reveal key={i} delay={i * 80}>
                <p className="text-lg text-ink-muted leading-relaxed">{para}</p>
              </Reveal>
            ))}
            {w.note && <p className="text-sm text-ink-muted">{w.note}</p>}
          </div>
        </div>
      </section>

      <section className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionLabel num="02">What we built</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
            Features in {w.name}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {w.built.map((b, i) => (
              <Reveal key={b.k} delay={i * 60}>
                <div className="h-full rounded-[16px] border border-white/[0.12] bg-ink-bg p-6">
                  <h3 className="text-xl font-semibold tracking-tight">{b.k}</h3>
                  <p className="mt-3 text-sm text-ink-muted leading-relaxed">{b.v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="work-screenshots" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionLabel num="03">Screenshots</SectionLabel>
          <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight">{w.name} screenshots</h2>
          <p className="mt-4 text-ink-muted max-w-2xl">
            Captured from the live product at{' '}
            <a href={w.url} target="_blank" rel="noopener" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white">
              {w.domain}
            </a>
            .
          </p>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {desktop.slice(1).map((s) => (
              <Shot key={s.src} shot={s} name={w.name} />
            ))}
          </div>
          {mobile.length > 0 && (
            <div className="mt-12 flex flex-wrap gap-8">
              {mobile.map((s) => (
                <Shot key={s.src} shot={s} name={w.name} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-14">
          <div>
            <SectionLabel num="04">Technology</SectionLabel>
            <h2 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">Stack and platform</h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {w.stack.map((x) => (
                <span key={x} className="text-sm px-3.5 py-1.5 rounded-full border border-white/[0.12] text-white/85">{x}</span>
              ))}
            </div>
          </div>
          <div>
            <SectionLabel num="04">Services</SectionLabel>
            <h2 className="mt-6 text-2xl md:text-3xl font-bold tracking-tight">Services used on this project</h2>
            <ul className="mt-6 space-y-3">
              {w.serviceLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="arrow-link inline-flex items-center gap-2 text-white/85 hover:text-white">
                    {l.label} <span className="arrow text-ink-muted">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="relative py-20 md:py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionLabel num="05">More work</SectionLabel>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((p) => (
              <li key={p.slug}>
                <Link to={`/work/${p.slug}/`} className="group block h-full rounded-[16px] border border-white/[0.12] bg-ink-surface p-6 hover:border-white/25 transition-colors">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-ink-muted">{p.sector}</div>
                  <div className="mt-3 text-xl font-semibold tracking-tight">{p.name}</div>
                  <span className="mt-4 inline-block text-sm text-violet">Case study →</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTABand
        eyebrow="Want something like this?"
        headline={`Let’s talk about your version of ${w.name}.`}
        sub="A free 30-minute call on WhatsApp. You get a fixed written quote."
      />
    </div>
  );
}
